import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Veille réglementaire & pédagogique (CDC §6.11, écran `ped-veille`).
 *
 * L'API rend les échéances avec leur état et leur date d'alerte, les évolutions
 * relevées avec leur analyse, l'état des sources du robot ; chaque ligne porte
 * ce qui empêche un geste. L'écran ne recalcule rien.
 */

export type TypeEcheance = 'RNCP' | 'RS' | 'AGREMENT' | 'CERTIFICATION_QUALITE' | 'HABILITATION' | 'AUTRE';
export type NiveauDetection = 'CRITIQUE' | 'IMPORTANT' | 'INFORMATION';
export type ImpactDetection = 'IMPACT' | 'SANS_IMPACT';
export type SourceVeille = 'FRANCE_COMPETENCES' | 'LISTE_PUBLIQUE_OF' | 'SAISIE';

export const TYPES_ECHEANCE: { valeur: TypeEcheance; libelle: string }[] = [
  { valeur: 'AGREMENT', libelle: 'Agrément' },
  { valeur: 'CERTIFICATION_QUALITE', libelle: 'Certification qualité' },
  { valeur: 'HABILITATION', libelle: 'Habilitation' },
  { valeur: 'RNCP', libelle: 'Fiche RNCP' },
  { valeur: 'RS', libelle: 'Fiche RS' },
  { valeur: 'AUTRE', libelle: 'Autre échéance' },
];

export const NIVEAUX_DETECTION: { valeur: NiveauDetection; libelle: string }[] = [
  { valeur: 'CRITIQUE', libelle: 'Critique' },
  { valeur: 'IMPORTANT', libelle: 'Important' },
  { valeur: 'INFORMATION', libelle: 'Information' },
];

export interface LigneEcheance {
  id: string;
  type: TypeEcheance;
  typeLibelle: string;
  intitule: string;
  reference: string | null;
  referenceLisible: string | null;
  lien: string | null;
  nature: string;
  dateEcheance: string | null;
  autorite: string | null;
  notes: string | null;
  origine: 'SAISIE' | 'ROBOT';
  siteCode: string | null;
  verifieeLe: string | null;
  formations: { code: string; titre: string }[];
  etat: 'A_RELEVER' | 'DEPASSEE' | 'EN_ALERTE' | 'ALERTE_PROCHE' | 'ALERTE_A_VENIR' | 'SOUS_SURVEILLANCE';
  etatLibelle: string;
  etatTon: string;
  dateAlerte: string | null;
  jours: number | null;
  creePar: string | null;
  modifiePar: string | null;
  modifieLe: string;
  refusModification: string | null;
  refusSuppression: string | null;
}

export interface LigneDetection {
  id: string;
  source: SourceVeille;
  sourceLibelle: string;
  reference: string | null;
  referenceLisible: string | null;
  nature: string;
  niveau: NiveauDetection;
  niveauLibelle: string;
  ton: 'danger' | 'warning' | 'info';
  titre: string;
  detail: string;
  avant: string | null;
  apres: string | null;
  lien: string | null;
  survenueLe: string | null;
  detecteeLe: string;
  statut: 'A_ANALYSER' | 'ANALYSEE';
  impact: ImpactDetection | null;
  impactLibelle: string | null;
  analyse: string | null;
  analyseeLe: string | null;
  analyseePar: string | null;
  saisiePar: string | null;
  refusSuppression: string | null;
}

export interface EtatSource {
  source: 'FRANCE_COMPETENCES' | 'LISTE_PUBLIQUE_OF';
  libelle: string;
  references: string[];
  dernierPassage: {
    debuteLe: string;
    termineLe: string | null;
    succes: boolean | null;
    jeu: string | null;
    message: string | null;
    detections: number;
    lancePar: string | null;
  } | null;
  dernierSucces: string | null;
}

export interface ReponseVeille {
  echeances: LigneEcheance[];
  detections: LigneDetection[];
  compteurs: { echeances: number; enAlerte: number; depassees: number; aAnalyser: number };
  sources: EtatSource[];
  analyseEnCours: boolean;
  preavisMois: number;
  sitesSaisissables: string[];
}

export interface SaisieEcheance {
  type: TypeEcheance;
  intitule: string;
  reference: string | null;
  nature: string;
  dateEcheance: string | null;
  autorite: string | null;
  notes: string | null;
  siteCode: string | null;
}

export interface SaisieDetection {
  titre: string;
  detail: string;
  sourceLibelle: string;
  niveau: NiveauDetection;
  reference: string | null;
  lien: string | null;
  survenueLe: string | null;
}

function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

function invalider(client: QueryClient): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['veille-reglementaire'] }),
    client.invalidateQueries({ queryKey: ['dashboard-pedagogique'] }),
    client.invalidateQueries({ queryKey: ['alertes'] }),
  ]);
}

export function useVeille(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['veille-reglementaire', parametres],
    queryFn: () => appelApi<ReponseVeille>(`/veille${parametres}`),
  });
}

export function useEnregistrerEcheance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saisie }: { id: string | null; saisie: SaisieEcheance }) =>
      id
        ? appelApi<LigneEcheance>(`/veille/echeances/${encodeURIComponent(id)}`, { methode: 'PATCH', corps: saisie })
        : appelApi<LigneEcheance>('/veille/echeances', { methode: 'POST', corps: saisie }),
    onSuccess: () => invalider(client),
  });
}

export function useSupprimerEcheance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<void>(`/veille/echeances/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

export function useSaisirDetection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieDetection) =>
      appelApi<LigneDetection>('/veille/detections', { methode: 'POST', corps: saisie }),
    onSuccess: () => invalider(client),
  });
}

export function useAnalyserDetection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, impact, analyse }: { id: string; impact: ImpactDetection; analyse: string | null }) =>
      appelApi<LigneDetection>(`/veille/detections/${encodeURIComponent(id)}/analyse`, {
        methode: 'PATCH',
        corps: { impact, analyse },
      }),
    onSuccess: () => invalider(client),
  });
}

export function useSupprimerDetection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<void>(`/veille/detections/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
    onSuccess: () => invalider(client),
  });
}

export function useLancerAnalyse() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => appelApi<{ message: string; detections: number }>('/veille/analyse', { methode: 'POST' }),
    onSettled: () => invalider(client),
  });
}
